import {
  Box,
  Button,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { PREDEFINED_CLAIM_FILTERS, REVIEW_VIEW } from "../../status";
import {
  type BackendFilters,
  createDefaultBackendFilters,
} from "../../utils/backendFilters";
import { useConfig } from "../../utils/config";
import useDevMode from "../../utils/devmode";
import { FEATURE_FLAG_KEYS, useFeatureFlag } from "../../utils/featureFlags";
import ClaimsTable, { type ClaimsTablePagination } from "../ClaimsTable";
import { getReviewColumnVisibility } from "../ClaimsTable/ClaimsTableColumns";
import type { ColumnVisibilityModel } from "../DataTable";

const CLAIMS_LIST_COLUMN_ORDER = [
  "urgency",
  "claimStatus",
  "availableSince",
  "practiceDisplayName",
  "payer",
  "wieldyPatientName",
  "claimId",
  "dateOfService",
  "wieldyClaimDate",
  "claimPayerPays",
  "postedState",
  "postedDateTime",
  "denialStatus",
  "action",
  "denialUrgency",
  "doneButton",
  "takeActionButton",
  "daysSinceNotice",
  "daysSinceService",
  "documents",
  "sources",
];

interface ClaimsListViewProps {
  claims: ClaimWithProcedureAndPatientMessage[];
  loading: boolean;
  updateClaim: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  onSaveClaim: (claim: ClaimMessage) => Promise<void>;
  onDeleteClaim: (claim: ClaimWithProcedureAndPatientMessage) => Promise<void>;
  onSaveProcedure: (procedure: ProcedureMessage) => Promise<void>;
  onDeleteProcedure: (procedure: ProcedureMessage) => Promise<void>;
  postToPMS: (claim: ClaimWithProcedureAndPatientMessage) => void;
  batchPostPMS: () => void;
  isBatchPosting: boolean;
  selectedPractices: PracticeMessage[];
  supportedPayers: CredentialsSupportedPayersMessage[];
  supportedSources: SourceMessage[];
  selectedFilter: PREDEFINED_CLAIM_FILTERS;
  setSelectedFilter: (filter: PREDEFINED_CLAIM_FILTERS) => void;
  activeView: REVIEW_VIEW | null;
  onViewChange: (activeView: REVIEW_VIEW) => void;
  onFilterChange?: (filters: BackendFilters) => void;
  pagination?: ClaimsTablePagination;
  onSortChange?: (field: string | null, direction: "asc" | "desc") => void;
  currentFilters?: BackendFilters;
  validateClaim: (claim: ClaimWithProcedureAndPatientMessage) => Promise<void>;
  onDone?: (
    claim: ClaimWithProcedureAndPatientMessage,
    claimWork: ClaimWork,
  ) => void;
  onDenialUpdate?: (
    claim: ClaimWithProcedureAndPatientMessage,
    denialWork: DenialWork,
  ) => Promise<void>;
  laterClaims?: ClaimWithProcedureAndPatientMessage[];
  laterLoading?: boolean;
  laterPagination?: ClaimsTablePagination;
}

export default function ClaimsListView({
  claims,
  loading,
  updateClaim,
  onSaveClaim,
  onDeleteClaim,
  onSaveProcedure,
  onDeleteProcedure,
  postToPMS,
  batchPostPMS,
  isBatchPosting,
  selectedPractices,
  supportedPayers,
  supportedSources,
  selectedFilter,
  setSelectedFilter,
  activeView,
  onViewChange,
  onFilterChange,
  pagination,
  onSortChange,
  validateClaim,
  onDone,
  onDenialUpdate,
  laterClaims = [],
  laterLoading = false,
  laterPagination,
  currentFilters = createDefaultBackendFilters(),
}: ClaimsListViewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const devMode = useDevMode();
  const { isEnabled: isDeniseEnabled } = useFeatureFlag(
    FEATURE_FLAG_KEYS.DENISE,
  );
  const { config } = useConfig();
  const isSplitDeniseView =
    isDeniseEnabled &&
    selectedFilter === PREDEFINED_CLAIM_FILTERS.REVIEW &&
    activeView === REVIEW_VIEW.DENISE;

  const [sharedColumnVisibility, setSharedColumnVisibility] =
    React.useState<ColumnVisibilityModel>(() =>
      getReviewColumnVisibility(isDeniseEnabled, activeView),
    );
  const [sharedColumnWidths, setSharedColumnWidths] = React.useState<
    Record<string, number>
  >({});
  React.useEffect(() => {
    setSharedColumnVisibility(
      getReviewColumnVisibility(isDeniseEnabled, activeView),
    );
  }, [isDeniseEnabled, activeView]);

  const filterFromQueryParam = (): PREDEFINED_CLAIM_FILTERS => {
    const statusParam = searchParams.get("status");
    if (!statusParam) {
      return PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS;
    }
    const formattedStatus =
      statusParam.charAt(0).toUpperCase() + statusParam.slice(1).toLowerCase();
    return Object.values(PREDEFINED_CLAIM_FILTERS).includes(
      formattedStatus as PREDEFINED_CLAIM_FILTERS,
    )
      ? (formattedStatus as PREDEFINED_CLAIM_FILTERS)
      : PREDEFINED_CLAIM_FILTERS.ALL_CLAIMS;
  };

  const getInitialSortingModel = () => {
    if (isDeniseEnabled && selectedFilter === PREDEFINED_CLAIM_FILTERS.REVIEW) {
      return [];
    }
    if (
      [
        PREDEFINED_CLAIM_FILTERS.REVIEW,
        PREDEFINED_CLAIM_FILTERS.DENIALS,
      ].includes(selectedFilter)
    ) {
      return [
        { field: "availableSince", sort: "asc" as const },
        { field: "urgency", sort: "desc" as const },
      ];
    }
    return [
      { field: "postedDateTime", sort: "desc" as const },
      { field: "urgency", sort: "desc" as const },
      { field: "availableSince", sort: "desc" as const },
    ];
  };

  const showViewSelector =
    isDeniseEnabled && selectedFilter === PREDEFINED_CLAIM_FILTERS.REVIEW;

  const claimKeyParam = searchParams.get("claim_key");
  const filteredClaimId =
    [...claims, ...laterClaims].find((c) => c.wieldyId === claimKeyParam)
      ?.claimId ?? claimKeyParam;
  const clearClaimKeyFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete("claim_key");
    navigate({ search: params.toString() }, { replace: true });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: adding filterFromQueryParam, setSelectedFilter would change when this runs
  React.useEffect(() => {
    setSelectedFilter(filterFromQueryParam());
  }, []);

  return (
    <>
      <h1>Claims</h1>
      {devMode ? (
        <div>
          <Button
            variant="contained"
            onClick={batchPostPMS}
            disabled={isBatchPosting}
            sx={{ width: "auto", minWidth: "7.5rem", marginBottom: "1rem" }}
          >
            {!isBatchPosting ? (
              "Batch Post"
            ) : (
              <CircularProgress color="inherit" size={24} />
            )}
          </Button>
        </div>
      ) : null}
      <ToggleButtonGroup
        size="medium"
        color="primary"
        value={selectedFilter}
        exclusive
        onChange={(_, newfilter) => {
          if (newfilter && selectedFilter !== newfilter) {
            const params = new URLSearchParams(location.search);
            params.set("status", newfilter.toLowerCase());
            navigate({ search: params.toString() }, { replace: true });
            setSelectedFilter(newfilter);
          }
        }}
        aria-label="Claim Predefine Filters"
      >
        {Object.values(PREDEFINED_CLAIM_FILTERS).map((filter) => {
          let label: string = filter;
          if (filter === PREDEFINED_CLAIM_FILTERS.DENIALS && isDeniseEnabled) {
            label = "Pending";
          }
          if (filter === PREDEFINED_CLAIM_FILTERS.REVIEW && isDeniseEnabled) {
            label = "Needs Work";
          }
          return (
            <ToggleButton
              key={filter}
              value={filter}
              aria-label={`${label} filter`}
            >
              {label}
            </ToggleButton>
          );
        })}
      </ToggleButtonGroup>
      {showViewSelector && (
        <ToggleButtonGroup
          size="small"
          value={activeView}
          exclusive
          onChange={(_, newView) => {
            if (newView && newView !== activeView) {
              const params = new URLSearchParams(location.search);
              params.set("view", newView);
              navigate({ search: params.toString() }, { replace: true });
              onViewChange(newView);
            }
          }}
          aria-label="Review views"
          sx={{
            marginTop: 2,
            display: "flex",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              border: "1px solid",
              borderColor: "divider",
              color: "text.secondary",
              "&.Mui-selected": {
                backgroundColor: "secondary.main",
                color: "secondary.contrastText",
                borderColor: "secondary.main",
                "&:hover": {
                  backgroundColor: "secondary.dark",
                },
              },
            },
          }}
        >
          <ToggleButton value={REVIEW_VIEW.DENISE}>Denise</ToggleButton>
          <ToggleButton value={REVIEW_VIEW.POSTIE}>Postie</ToggleButton>
        </ToggleButtonGroup>
      )}
      {claimKeyParam && (
        <Box sx={{ marginTop: 2 }}>
          <Chip
            label={`Filtered: ${filteredClaimId}`}
            onDelete={clearClaimKeyFilter}
            aria-label={`Clear claim filter ${filteredClaimId}`}
          />
        </Box>
      )}
      <Grid
        container
        spacing={2}
        style={{ marginTop: "27px", padding: "0px 2px" }}
      >
        {isSplitDeniseView && (
          <Grid item xs={12} sx={{ marginBottom: -1 }}>
            <Typography variant="h6">
              Due Now · {pagination?.rowCount ?? 0}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Work this queue first
            </Typography>
          </Grid>
        )}
        <Grid item xs={12}>
          <ClaimsTable
            initialSortingModel={getInitialSortingModel()}
            payment={null}
            enablePaymentNavigation
            claims={claims}
            isLoading={loading}
            onClaimUpdate={updateClaim}
            onPostToPMS={postToPMS}
            enableClaimDetails
            excludeByFieldNames={["paymentId", "notes"]}
            sortColumnOrder={CLAIMS_LIST_COLUMN_ORDER}
            onSaveClaim={onSaveClaim}
            onDeleteClaim={onDeleteClaim}
            onSaveProcedure={onSaveProcedure}
            onDeleteProcedure={onDeleteProcedure}
            practices={selectedPractices}
            supportedPayers={supportedPayers}
            supportedSources={supportedSources}
            persistKey="claimsListView"
            onFilterChange={onFilterChange}
            pagination={pagination}
            onSortChange={onSortChange}
            currentFilters={currentFilters}
            currentTab={selectedFilter}
            activeView={activeView}
            validateClaim={validateClaim}
            onDone={onDone}
            onDenialUpdate={onDenialUpdate}
            columnVisibilityModel={
              isSplitDeniseView ? sharedColumnVisibility : undefined
            }
            onColumnVisibilityModelChange={
              isSplitDeniseView ? setSharedColumnVisibility : undefined
            }
            columnWidths={isSplitDeniseView ? sharedColumnWidths : undefined}
            onColumnWidthsChange={
              isSplitDeniseView ? setSharedColumnWidths : undefined
            }
          />
        </Grid>
        {isSplitDeniseView && (
          <>
            <Grid item xs={12} sx={{ marginTop: 2, marginBottom: -1 }}>
              <Typography variant="h6">
                Later · {laterPagination?.rowCount ?? 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Medium / Low urgency with an action assigned — moves to Due Now
                after {config.deniseDueNowDaysThreshold} days open
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <ClaimsTable
                initialSortingModel={getInitialSortingModel()}
                payment={null}
                enablePaymentNavigation
                claims={laterClaims}
                isLoading={laterLoading}
                onClaimUpdate={updateClaim}
                onPostToPMS={postToPMS}
                enableClaimDetails
                excludeByFieldNames={["paymentId", "notes"]}
                sortColumnOrder={CLAIMS_LIST_COLUMN_ORDER}
                onDeleteClaim={onDeleteClaim}
                onSaveProcedure={onSaveProcedure}
                onDeleteProcedure={onDeleteProcedure}
                practices={selectedPractices}
                supportedPayers={supportedPayers}
                supportedSources={supportedSources}
                pagination={laterPagination}
                currentFilters={currentFilters}
                currentTab={selectedFilter}
                activeView={activeView}
                validateClaim={validateClaim}
                onDenialUpdate={onDenialUpdate}
                hideToolbar
                hideColumnHeaders
                columnVisibilityModel={sharedColumnVisibility}
                columnWidths={sharedColumnWidths}
              />
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
}
